import { styled } from '@uhg-abyss/web/tools/styled';
import React, { useEffect, useState } from 'react';
import { PageLayout } from '@src/common/PageLayout';
import { Button } from '@src/common/Button';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useSteps } from '@src/state/steps';
import { event as TaggingEvent } from '@uhg-abyss/web/tools/event';
import { useUser } from '@src/state/user/hooks';
import isEmpty from 'lodash/isEmpty';
import { getSessionRequestObj } from '@src/state/tinRegistration/utils';
import { useTinRegistration } from '@src/state/tinRegistration';
import { Loading } from '@src/common/Loading';
import { useTinDrafts } from '@src/state/superUserDraft';
import { storage } from '@uhg-abyss/web/tools/storage';
import { useForm as useAbyssForm } from '@uhg-abyss/web/hooks/useForm';
import { FileUpload as FileUploadComp } from '@src/common/FileUploadV2';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Name } from './Inputs/Name';
import { FederalTaxClassification } from './FederalTaxClassification';
import { BusinessName } from './Inputs/BusinessName';
import { TinHeader } from '../TinHeader';

const PageButtons = styled.div({
  width: '11%',
  marginTop: '2em',
  paddingBottom: '25px',
  fontWeight: 600,
  fontSize: '16px',
  color: '$primary1',
  fontFamily: '$heading',
  display: 'inline-flex',
  marginRight: '15px',
});

const PageDraftButton = styled('div', {
  width: 'fitContent',
  marginTop: '2em',
  paddingBottom: '25px',
  float: 'right',
  color: '$primary1',
});

const PageDraftButtonFont = styled('div', {
  fontSize: '16px',
  fontWeight: 600,
  fontFamily: '$heading',
});

const TextStyle = styled('div', {
  margin: '10px 10px 10px 10px',
});

const SaveDraftSuccessMessage = styled('div', {
  fontSize: '13px',
  color: '$success1',
  textAlign: 'center',
});

const SaveDraftFailedMessage = styled('div', {
  fontSize: '13px',
  textAlign: 'center',
  color: '$error1',
});

export const W9Upload = () => {
  const abyssForm = useAbyssForm();
  const registration = abyssForm.getValues('searchRegistration');
  const userInfo = useUser.state();
  const practiceInfo = abyssForm.getValues('practiceInformation');
  const address = abyssForm.getValues('address');
  const signatureType = abyssForm.getValues('attestation.signatureRadio');
  const attestation = abyssForm.getValues('attestation');
  const { documentIds, trackingNumber, tinDraft, isDraftDisabled } =
    useTinRegistration.state();
  const [isDataLoading, setDataLoading] = useState(false);
  const { saveAsDraft } = useTinRegistration.actions();
  const {
    pagesVisited,
    pagesSubmitted,
    setStep,
    addPageVisited,
    addPageSubmitted,
  } = useSteps();
  const { saveSuperUserDraft } = useTinDrafts.actions();
  const isSuperUser = storage.local.get(userInfo.uuid);

  const router = useRouter();

  useEffect(() => {
    const pageName = 'epvsecure:tin registration: step1-w9 details';
    const billingTin = registration ? registration.billingTin : '';
    const siteSectionLevel = {
      siteSectionLevel1: 'tin registration',
      siteSectionLevel2: 'step1-w9 details',
      siteSectionLevel3: '',
      siteSectionLevel4: '',
      siteSectionLevel5: '',
    };
    TaggingEvent('ON_PAGE_LOAD', {
      userInfo,
      pageName,
      billingTin,
      siteSectionLevel,
    });
    if (pagesVisited) {
      if (!pagesVisited.includes('W9Upload')) {
        const pages = pagesVisited.concat('W9Upload');
        addPageVisited(pages);
      }
    } else {
      const pages = ['W9Upload'];
      addPageVisited(pages);
    }
    setStep(1);
    if (
      tinDraft &&
      tinDraft.w9Details &&
      tinDraft.returnToRegistration &&
      !pagesSubmitted.includes('W9Upload')
    ) {
      setDataLoading(true);
      abyssForm.validate('w9form', (values) => {
        if (pagesSubmitted) {
          if (!pagesSubmitted.includes('W9Upload')) {
            const pages = pagesSubmitted.concat('W9Upload');
            addPageSubmitted(pages);
          }
        } else {
          const pages = ['W9Upload'];
          addPageSubmitted(pages);
        }
        router.navigate('/tin-registration/step/2');
      });
      setDataLoading(false);
    }
  }, []);

  const goBack = () => {
    TaggingEvent('W9_UPLOAD_BACK');
    router.navigate('/');
  };

  const [saveDraftFailed, setSaveDraftFailed] = useState(false);
  const [saveDraftSuccess, setSaveDraftSuccess] = useState(false);
  const saveDraft = () => {
    const reqBody = getSessionRequestObj({
      w9Form: abyssForm.getValues('w9form'),
      practiceInfo,
      address,
      attestation,
      documentIds,
      signatureType,
    });

    const interactionName = 'w9 details widget';
    const interactionValue = 'save as draft';
    const eventInteraction = 'interaction';
    TaggingEvent('SAVE_AS_DRAFT', {
      eventInteraction,
      interactionName,
      interactionValue,
    });

    const billingTin = registration ? registration.billingTin : '';
    const requestId = registration ? registration.requestId : '';
    const tNum = trackingNumber;
    setSaveDraftSuccess(false);
    setSaveDraftFailed(false);
    if (!isSuperUser) {
      saveAsDraft(
        {
          billingTin,
          requestId,
          trackingNumber: tNum,
          ...reqBody,
        },
        (response) => {
          setSaveDraftSuccess(true);
        },
        (error) => {
          setSaveDraftFailed(true);
        }
      );
    } else {
      saveSuperUserDraft(
        {
          billingTin,
          requestId,
          optumId: registration.optumId,
          trackingNumber: tNum,
          ...reqBody,
        },
        (response) => {
          setSaveDraftSuccess(true);
        },
        (error) => {
          setSaveDraftFailed(true);
        }
      );
    }
  };

  const continueRegistration = () => {
    abyssForm.validate(
      'w9form',
      () => {
        if (pagesSubmitted) {
          if (!pagesSubmitted.includes('W9Upload')) {
            const pages = pagesSubmitted.concat('W9Upload');
            addPageSubmitted(pages);
          }
        } else {
          const pages = ['W9Upload'];
          addPageSubmitted(pages);
        }
        TaggingEvent('W9_UPLOAD_CONTINUE');
        const eventData = {
          action: 'continue',
          pageName: 'step1-w9-details',
        };
        TaggingEvent('EAPM_TRANSACTION_CONTINUE', eventData);
        router.navigate('/tin-registration/step/2');
      },
      (error) => {
        const fields = {};
        if (isEmpty(error.fileName)) {
          fields.uploadW9Form = '';
        }
        if (isEmpty(error.name)) {
          fields.name = '';
        }
        if (isEmpty(error.federalTaxClassification)) {
          fields.federalTaxClassification = '';
        }
        if (!isEmpty(fields)) {
          TaggingEvent('FORM_VALIDATION_ERROR', { fields });
        }
      }
    );
  };

  return (
    <Loading isLoading={isDataLoading}>
      <PageLayout>
        <TinHeader />
        <Card header="W-9 Details">
          <Card.Section>
            <Layout.Stack alignItems="left">
              <TextStyle>
                <FileUploadComp
                  id="upload-w9form-input"
                  model="w9form.fileName"
                  label="Upload W-9 form"
                  required
                />
                <Name inputModel="w9form.name" />
                <BusinessName inputModel="w9form.businessName" />
                <FederalTaxClassification inputModel="w9form.federalTaxClassification" />
              </TextStyle>
            </Layout.Stack>
          </Card.Section>
        </Card>
        <PageButtons>
          <Layout.Group>
            <Button
              type="button"
              id="w9-back-button"
              aria-label="click here to go back"
              data-testid="back-button"
              onClick={goBack}
              width="100%"
              variant="outline"
            >
              Back
            </Button>
            <Button
              type="button"
              id="w9-continue-button"
              aria-label="click here to continue"
              data-testid="continue-button"
              onClick={continueRegistration}
              width="100%"
            >
              Continue
            </Button>
          </Layout.Group>
        </PageButtons>
        <PageDraftButton>
          <PageDraftButtonFont>
            <Button
              type="button"
              id="draft-continue-button"
              aria-label="click here to save as draft"
              data-testid="draft-button"
              onClick={saveDraft}
              width="100%"
              variant="outline"
              isDisabled={isDraftDisabled}
            >
              Save as draft
            </Button>
          </PageDraftButtonFont>
          {saveDraftSuccess && (
            <SaveDraftSuccessMessage id="savedraft-success">
              Saved successfully.
            </SaveDraftSuccessMessage>
          )}
          {saveDraftFailed && (
            <SaveDraftFailedMessage id="savedraft-failed">
              Failed to save.
            </SaveDraftFailedMessage>
          )}
        </PageDraftButton>
      </PageLayout>
    </Loading>
  );
};
